'use client';

import { Button } from '@insta-club/ui';
import Link from 'next/link';

type status = 'success' | 'error' | 'warning' | 'info';

interface IProps {
    status: status;
    title: string;
    description: string;
    icon?: React.ReactNode;
    subTitle?: React.ReactElement | undefined | null;
    action: {
        label: string;
        href: string;
        onClick?: () => void;
    };
}

function ActionResult({ status, title, subTitle, description, action, icon }: IProps) {
    const renderActionIcon = (status: status) => {
        switch (status) {
            case 'success':
                return (
                    <svg width='149' height='119' viewBox='0 0 149 119' fill='none' xmlns='http://www.w3.org/2000/svg'>
                        <ellipse cx='82.4999' cy='71' rx='47.2836' ry='48' fill='#E4F7EA' />
                        <circle cx='82.5002' cy='70.2836' r='36.5373' fill='#BEEBCC' />
                        <path
                            d='M82.4994 47.1194C69.6701 47.1194 59.2158 57.8418 59.2158 71C59.2158 84.1582 69.6701 94.8806 82.4994 94.8806C95.3287 94.8806 105.783 84.1582 105.783 71C105.783 57.8418 95.3287 47.1194 82.4994 47.1194ZM93.629 65.5074L80.4272 79.0477C80.1012 79.3821 79.6588 79.5731 79.1931 79.5731C78.7275 79.5731 78.2851 79.3821 77.9591 79.0477L71.3698 72.2895C70.6946 71.597 70.6946 70.4507 71.3698 69.7582C72.0451 69.0657 73.1627 69.0657 73.8379 69.7582L79.1931 75.2507L91.1609 62.9761C91.8361 62.2836 92.9537 62.2836 93.629 62.9761C94.3042 63.6686 94.3042 64.791 93.629 65.5074Z'
                            fill='#00BC55'
                        />
                        <circle cx='6' cy='6' r='6' transform='matrix(-1 0 0 1 12 15)' fill='#C6C3FB' />
                        <circle cx='4' cy='4' r='4' transform='matrix(-1 0 0 1 64 0)' fill='#FED883' />
                        <circle cx='2' cy='2' r='2' transform='matrix(-1 0 0 1 20 75)' fill='#DBEAFE' />
                        <circle cx='3' cy='3' r='3' transform='matrix(-1 0 0 1 149 85)' fill='#FEB5BD' />
                    </svg>
                );
                break;
            case 'error':
                return (
                    <svg width='149' height='119' viewBox='0 0 149 119' fill='none' xmlns='http://www.w3.org/2000/svg'>
                        <ellipse cx='82.4999' cy='71' rx='47.2836' ry='48' fill='#FEEBEE' />
                        <circle cx='82.5002' cy='70.2835' r='36.5373' fill='#FCCDD3' />
                        <path
                            d='M82.4994 47.1196C69.6701 47.1196 59.2158 57.842 59.2158 71.0002C59.2158 84.1584 69.6701 94.8808 82.4994 94.8808C95.3287 94.8808 105.783 84.1584 105.783 71.0002C105.783 57.842 95.3287 47.1196 82.4994 47.1196ZM90.3227 76.4928C90.9979 77.1853 90.9979 78.3316 90.3227 79.0241C89.9734 79.3823 89.531 79.5495 89.0887 79.5495C88.6463 79.5495 88.2039 79.3823 87.8546 79.0241L82.4994 73.5316L77.1442 79.0241C76.7949 79.3823 76.3525 79.5495 75.9101 79.5495C75.4678 79.5495 75.0254 79.3823 74.6761 79.0241C74.0009 78.3316 74.0009 77.1853 74.6761 76.4928L80.0313 71.0002L74.6761 65.5077C74.0009 64.8152 74.0009 63.6689 74.6761 62.9763C75.3513 62.2838 76.469 62.2838 77.1442 62.9763L82.4994 68.4689L87.8546 62.9763C88.5299 62.2838 89.6475 62.2838 90.3227 62.9763C90.9979 63.6689 90.9979 64.8152 90.3227 65.5077L84.9675 71.0002L90.3227 76.4928Z'
                            fill='#FF3257'
                        />
                        <circle cx='6' cy='6' r='6' transform='matrix(-1 0 0 1 12 15)' fill='#C6C3FB' />
                        <circle cx='4' cy='4' r='4' transform='matrix(-1 0 0 1 64 0)' fill='#FED883' />
                        <circle cx='2' cy='2' r='2' transform='matrix(-1 0 0 1 20 75)' fill='#DBEAFE' />
                        <circle cx='3' cy='3' r='3' transform='matrix(-1 0 0 1 149 85)' fill='#FEB5BD' />
                    </svg>
                );
                break;
            case 'info':
                return 'info icon';
            case 'warning':
                return 'warning icon';
            default:
                break;
        }
    };
    return (
        <>
            <div className='flex h-full flex-col items-center justify-center space-y-8 text-center'>
                {icon ? icon : renderActionIcon(status)}
                <div className='space-y-3'>
                    <h4 className='text-base font-bold text-gray-800'>{title}</h4>
                    {subTitle ? subTitle : null}
                    <p className='text-sm leading-7 font-normal text-gray-500'>{description}</p>
                </div>
                <Link
                    href={action.href}
                    className='w-full'
                    onClick={() => {
                        action?.onClick?.();
                    }}
                >
                    <Button fullWidth>{action.label}</Button>
                </Link>
            </div>
        </>
    );
}

export default ActionResult;

